import { useEffect, useState } from "react";

import { attempt } from "~/api/attempt";
import { api } from "~/api/client";
import type { LandingEvent, LandingPayload, LandingWindow } from "~/api/types";
import { CalendarDoor } from "~/components/CalendarDoor";
import { FoldableSection } from "~/components/FoldableSection";
import type { MarksActions } from "~/components/MarksContext";
import { MarksProvider } from "~/components/MarksContext";
import { useViewer } from "~/Viewer";
import { JustAdded } from "~/components/JustAdded";
import { Tagline } from "~/components/Tagline";

import s from "./Highlights.module.css";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const MONS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Before this hour the first section is called Today, after it Tonight. A gig at 10.30 in
 * the morning under a heading saying TONIGHT is the page describing its own contents wrongly
 * — the night bucket is right, the word was not.
 */
const EVENING_FROM_HOUR = 17;

/**
 * And the small hours belong to the evening before, which is the same rule the server cuts
 * the night on at 05:00.
 *
 * `getHours() >= 17` alone is false at one in the morning, so the section read **TODAY** over
 * *yesterday's* date — the page contradicting the reader's own clock while the bucket beneath
 * it was right. Tonight is true there in the only sense anybody means it: the night you are
 * still in. The house rule is that a rule like this is carried by words that do not need
 * explaining rather than by a note on the page, and this is the word.
 */
const NIGHT_UNTIL_HOUR = 5;

/** A `YYYY-MM-DD` night, read as a date without letting the local zone shift it a day. */
function night(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`);
}

function day(iso: string, withMonth = true): string {
  const d = night(iso);
  const text = `${DAYS[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()}`;
  return withMonth ? `${text} ${MONS[d.getUTCMonth()]}` : text;
}

/** The nights a window covers, worded. A single night says the year; a span does not. */
function span(window: LandingWindow): string {
  if (window.first === window.last)
    return `${day(window.first)} ${night(window.first).getUTCFullYear()}`;
  const sameMonth = window.first.slice(0, 7) === window.last.slice(0, 7);
  return `${day(window.first, !sameMonth)} – ${day(window.last)}`;
}

/**
 * Where a section's "the rest" goes, in the grid that matches its own shape. A night is a
 * list, a week is the week grid, a month is the month grid — the window the reader was
 * looking at, continued, rather than dropped on whatever the calendar opens on by default.
 */
function rest(window: LandingWindow, grid: "list" | "week" | "month"): string {
  return `/calendar?view=picked&grid=${grid}&on=${window.first}`;
}

/**
 * "This month" while it still is one, and the month's name once it is not.
 *
 * The window rolls forward when the current month is spent — on the 30th there is no use in
 * a section headed August holding nothing — and at that point "this month" would be pointing
 * at September, which is next month. Naming it is the only honest thing left.
 */
function monthHeading(first: string): string {
  const window = night(first);
  const now = new Date();
  const current =
    window.getUTCFullYear() === now.getFullYear() &&
    window.getUTCMonth() === now.getMonth();
  return current ? "This month" : (MONTHS[window.getUTCMonth()] ?? "");
}

export function Highlights() {
  const [payload, setPayload] = useState<LandingPayload | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const me = useViewer() ?? null;

  /*
   * Patched here rather than through the calendar's rows.
   *
   * `useCalendar` holds the calendar's payload, and this page holds its own — four windows
   * of curated gigs, fetched from `/landing`. Routing a heart through the calendar would
   * write to a list this page does not read, so the request would succeed and the card
   * would not change, which is indistinguishable from the control not working.
   */
  const mark = (id: string, patch: (e: LandingEvent) => LandingEvent) =>
    setPayload((was) =>
      was === null
        ? was
        : {
            ...was,
            tonight: {
              ...was.tonight,
              events: was.tonight.events.map((e) =>
                e.id === id ? patch(e) : e,
              ),
            },
            week: {
              ...was.week,
              events: was.week.events.map((e) => (e.id === id ? patch(e) : e)),
            },
            month: {
              ...was.month,
              events: was.month.events.map((e) => (e.id === id ? patch(e) : e)),
            },
          },
    );

  /** Following an act adopts every gig they headline, so every card with them on it moves. */
  const markAct = (artistId: number, on: boolean) =>
    setPayload((was) => {
      if (was === null) return was;
      const touch = (e: LandingEvent) =>
        e.act?.id === artistId
          ? { ...e, starred: on, picked: on || Boolean(e.picked) }
          : e;
      return {
        ...was,
        tonight: { ...was.tonight, events: was.tonight.events.map(touch) },
        week: { ...was.week, events: was.week.events.map(touch) },
        month: { ...was.month, events: was.month.events.map(touch) },
      };
    });

  /*
   * What pressing a mark does here.
   *
   * Handed to the cards through context rather than passed down, so every card on this page
   * gets its three whether it is in a fold, in "just added", or somewhere added later.
   *
   * The patch is this page's own. `useCalendar` holds the calendar's rows and this page
   * holds four windows from `/landing`; routing a heart through the calendar would write to
   * a list this page does not read, so the request would succeed and the card would not
   * change — indistinguishable from the control not working.
   */
  /*
   * Patched first, written second: the mark answers the press at once rather than after a
   * round trip through the tunnel. When the write does not take, `attempt` has said so on
   * screen and the mark is put back — `liked`, `going` or `starred` to what it was, and only
   * that: `picked` and going-implies-liking are left as the press set them, because the page
   * cannot know their prior value without a snapshot and the sentence already says the press
   * did not take.
   */
  const acting: MarksActions = {
    me,
    save: (id, on) => {
      mark(id, (e) => ({ ...e, liked: on }));
      void attempt(() => api.save(id, on)).then((took) => {
        if (!took) mark(id, (e) => ({ ...e, liked: !on }));
      });
    },
    going: (id, on) => {
      // Saying you are going puts it on the list; unsaying it does not take it off.
      mark(id, (e) => ({ ...e, going: on, liked: on || Boolean(e.liked) }));
      void attempt(() => api.going(id, on)).then((took) => {
        if (!took) mark(id, (e) => ({ ...e, going: !on }));
      });
    },
    star: (artistId, on) => {
      markAct(artistId, on);
      void attempt(() => api.star(artistId, on)).then((took) => {
        if (!took) markAct(artistId, !on);
      });
    },
  };

  useEffect(() => {
    let live = true;
    api
      .landing()
      .then((data) => live && setPayload(data))
      .catch((error: unknown) => {
        if (live)
          setFailed(error instanceof Error ? error.message : String(error));
      });
    return () => {
      live = false;
    };
  }, []);

  if (failed) {
    return (
      <p className={s.trouble}>The calendar could not be reached. {failed}</p>
    );
  }

  if (!payload) return <main aria-busy="true" />;

  const hour = new Date().getHours();
  const evening = hour >= EVENING_FROM_HOUR || hour < NIGHT_UNTIL_HOUR;
  const first = evening ? "Tonight" : "Today";
  const monthName = monthHeading(payload.month.first);

  return (
    <MarksProvider value={acting}>
      <main>
        {/* The tagline belongs to this view, not to the shell: it says what the page below it
          is, and the calendar will say something else. */}
        <div className={s.say}>
          <Tagline kind="landing" count={payload.counts.events} />
        </div>

        <FoldableSection
          id="tonight"
          heading={first}
          meta={`${day(payload.tonight.first)} ${night(payload.tonight.first).getUTCFullYear()}`}
          events={payload.tonight.events}
          hasMore={payload.tonight.hasMore}
          moreHref={rest(payload.tonight, "list")}
          doneText={`that is all of ${first.toLowerCase()}`}
          eager
          lead
        />
        <FoldableSection
          id="week"
          heading="This week"
          meta={span(payload.week)}
          events={payload.week.events}
          hasMore={payload.week.hasMore}
          moreHref={rest(payload.week, "week")}
          doneText="that is all of this week"
        />
        <FoldableSection
          id="month"
          heading={monthName}
          meta={span(payload.month)}
          events={payload.month.events}
          hasMore={payload.month.hasMore}
          moreHref={rest(payload.month, "month")}
          doneText={`that is all of ${monthName.toLowerCase()}`}
        />
        <JustAdded
          months={payload.justAdded.months}
          hasMore={payload.justAdded.hasMore}
        />

        <CalendarDoor events={payload.counts.events} />
      </main>
    </MarksProvider>
  );
}
